import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageCheck, RotateCcw, Truck } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { itemsFmt } from "@/components/ranger/Crud";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const STATE = { delivered: "connected", pending_delivery: "unconfigured", refunded: "error" };

export const Orders = ({ cid, kind }) => {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/communities/${cid}/orders`).then((r) => setRows(r.data.filter((o) => o.kind === kind))), [cid, kind]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => { try { await api.post(`/communities/${cid}/orders/${id}/${action}`); toast.success("Done"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const btn = "text-slate-300 hover:text-white";
  return (
    <Card title={kind === "shop" ? "Purchase history" : "Redemption history"} testId={`${kind}-orders-card`}>
      {rows.length === 0 ? <EmptyState title="No orders yet" testId={`${kind}-orders-empty`}>Orders appear when linked players buy or redeem from the player portal.</EmptyState> : (
        <div className="overflow-x-auto">
          <Table data-testid={`${kind}-orders-table`}>
            <TableHeader><TableRow className="border-[#1E2D4A]">{["Time", "Player", "Item", "Contents", "Price", "Status", "Delivery", ""].map((h) => <TableHead key={h} className="rg-label whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{rows.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`} className="border-[#1E2D4A]/60 text-xs">
                <TableCell className="whitespace-nowrap text-slate-400">{fmtTime(o.created_at)}</TableCell>
                <TableCell className="text-slate-200">{o.gamertag}</TableCell>
                <TableCell className="text-slate-200">{o.name}</TableCell>
                <TableCell className="font-mono text-slate-400">{itemsFmt(o.items)}</TableCell>
                <TableCell className="font-mono text-slate-300">{o.price}</TableCell>
                <TableCell><StatusBadge state={STATE[o.status]} label={o.status.replace("_", " ")} /></TableCell>
                <TableCell className="max-w-xs text-slate-400">{o.delivery_result || "—"}</TableCell>
                <TableCell>{can("admin") && o.status === "pending_delivery" && <div className="flex gap-2">
                  <button data-testid={`order-deliver-${o.id}`} title="Deliver now via RCON" onClick={() => act(o.id, "deliver")} className={btn}><Truck className="h-4 w-4" /></button>
                  <button data-testid={`order-mark-${o.id}`} title="Mark delivered" onClick={() => act(o.id, "mark-delivered")} className={btn}><PackageCheck className="h-4 w-4" /></button>
                  <ConfirmButton testId={`order-refund-${o.id}`} className={btn} title="Refund order?" description={`Return ${o.price} to ${o.gamertag}.`} onConfirm={() => act(o.id, "refund")}><RotateCcw className="h-4 w-4" /></ConfirmButton>
                </div>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
};
